<template lang="pug">
.add-gym-information
  StepHeader(
    :title="title"
    :description="description")
  .training-types
    button.training-type(
      type="button"
      v-for="trainingType in TRAINING_TYPES"
      :class="{selected: modelValue.trainingTypes?.[0] === trainingType || (!isPrimary && (modelValue.trainingTypes || []).includes(trainingType)), disabled: !isPrimary && modelValue.trainingTypes?.[0] === trainingType}"
      @click="toggleTrainingTypeSelection(trainingType)"
      :disabled="!isPrimary && modelValue.trainingTypes?.[0] === trainingType"
    ) {{ __(trainingType) }}
</template>
<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { MARKET_SEGMENTS, type IGym } from '@rogue/universal'
import StepHeader from './StepHeader.vue'
import { useRogue } from '@/js/services/Rogue'
import { useCustomerStore } from '@/js/stores/customerStore'

const rogue = useRogue()

const props = defineProps<{
  modelValue: Partial<IGym>,
  isPrimary: boolean,
  step: number,
  isEdit: boolean,
}>()

const emit = defineEmits<{(e: 'update:modelValue', val: Partial<IGym>): void }>()

const customerStore = useCustomerStore()

const TRAINING_TYPES: string[] = Object.keys(MARKET_SEGMENTS[rogue.site.id].Interests)

const customerSegments = computed<number[]>(() => customerStore.segments || [])

const title = computed<string>(() => `Step ${props.step}: ${(props.isPrimary ? 'Select Your Primary Training Type' : 'Select Any Other Training Types')}`)
const description = computed<string>(() => (props.isPrimary ? 'Select a training type that best represents your main focus.' : 'Select any other training types that interest you.'))

const isValid = computed<boolean>(() => !!props.modelValue.trainingTypes?.[0])

function toggleTrainingTypeSelection(trainingType: string) {
  const updatedGym: Partial<IGym> = Object.assign(props.modelValue)
  updatedGym.trainingTypes = updatedGym.trainingTypes || []

  if (props.isPrimary) {
    // Primary training type is stored at the first position in the array.
    if (!updatedGym.trainingTypes?.length) {
      updatedGym.trainingTypes = [trainingType]
    } else if (updatedGym.trainingTypes[0] === trainingType) {
      // @ts-expect-error training types doesn't expect null
      updatedGym.trainingTypes[0] = null
    } else if (updatedGym.trainingTypes[0] !== trainingType) {
      updatedGym.trainingTypes[0] = trainingType
    }

    // Remove new primary training type from others list
    if (updatedGym.trainingTypes.includes(trainingType)) {
      updatedGym.trainingTypes = updatedGym.trainingTypes.filter((t, i) => t !== trainingType || i === 0)
    }
  } else if (!updatedGym.trainingTypes.includes(trainingType)) {
    updatedGym.trainingTypes.push(trainingType)
  } else if (updatedGym.trainingTypes.includes(trainingType)) {
    updatedGym.trainingTypes = updatedGym.trainingTypes.filter(t => t !== trainingType)
  }

  emit('update:modelValue', updatedGym)
}

onMounted(async () => {
  // If you are creating a home gym and haven't already selected a training type, preselect the customer's training types
  if (!props.isEdit && props.modelValue.type === 'Home Gym' && !props.modelValue.trainingTypes?.length) {
    if (!customerSegments.value.length) {
      await customerStore.init(rogue)
      await customerStore.loadCustomer(rogue)
    }

    const updatedGym: Partial<IGym> = Object.assign(props.modelValue)
    updatedGym.trainingTypes = Object.keys(MARKET_SEGMENTS[rogue.site.id].Interests)
      .filter(key => customerSegments.value.includes(MARKET_SEGMENTS[rogue.site.id].Interests[key]))
    emit('update:modelValue', updatedGym)
  }
})

defineExpose({
  isValid,
})
</script>
<style lang="scss" scoped>
.training-types {
  display: flex;
  flex-flow: row wrap;
  gap: $space-xs $space-s;

  .training-type {
    background-color: $color-white;
    border: 1px solid $color-gray;
    border-radius: 3px;
    color: $color-black;
    flex-grow: 1;
    flex-basis: calc(50% - $space-s);
    font-size: $size-xs;
    padding: $space-xs $space-s;

    &:hover {
      cursor: pointer;
    }

    &.selected {
      background-color: $color-black;
      color: $color-white;
    }

    &.disabled {
      background: rgba(0, 0, 0, 0.4);
    }

    @include mobile() {
      flex-basis: 100%;
    }
  }
}
</style>
